import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useEnv } from '../context'
import { onOnline, useOnline } from '../platform/network'
import type { ActivePhase, HubQueueEvent } from './hubQueue/queue'
import type { QueuedQuestion } from './hubQueue/store'
import type { HubMessage } from './types'

export { withContext } from './hubQueue/queue'

/**
 * Состояние чата Штаба в PWA (S13, SC09, SC12; этап 4, шаг 2).
 *
 * Различаем то, что ТЗ просит не смешивать: вопрос ждёт сети, уходит, не
 * отправлен (уйдёт сам, можно повторить), не распознан, упёрся в правило
 * тарифа; отправлен и ждёт ответа; ответ не дождались (вопрос принят,
 * проверить ещё раз).
 *
 * Каждый вопрос сначала записывается в очередь на устройстве и уходит из неё
 * (hubQueue): без сети, после сбоя и после закрытия приложения он не
 * теряется. Повтор идёт с тем же client_message_id - сервер не создаст второй
 * вопрос и не потратит второй запрос лимита.
 */

/**
 * waiting_network - нет сети, уйдёт сам;
 * sending, transcribing - уходит сейчас;
 * queued - ждёт, пока уйдёт вопрос перед ним;
 * retry - сорвалось по связи или серверу, уйдёт сам, можно повторить сейчас;
 * unrecognized, limit, failed - нужно решение человека.
 */
export type QueuedPhase =
  | 'waiting_network'
  | 'sending'
  | 'transcribing'
  | 'queued'
  | 'retry'
  | 'unrecognized'
  | 'limit'
  | 'failed'

export type HubItem =
  | { kind: 'message'; message: HubMessage }
  | { kind: 'queued'; question: QueuedQuestion; phase: QueuedPhase }

/** Ответ ещё готовится. */
export function replyPending(m: HubMessage): boolean {
  return m.role === 'assistant' && (m.status === 'pending' || m.status === 'processing')
}

export function queuedPhase(
  q: QueuedQuestion,
  active: { id: string; phase: ActivePhase } | null,
  online: boolean,
): QueuedPhase {
  if (q.state !== 'waiting') return q.state
  if (active?.id === q.id) return active.phase
  if (!online) return 'waiting_network'
  if (q.lastErrorAt) return 'retry'
  if (active) return 'queued'
  return q.kind === 'voice' && !q.text ? 'transcribing' : 'sending'
}

const ROOT = 'redesign'

export function useHubChat() {
  const { api, platform, hubQueue, toast } = useEnv()
  const online = useOnline()
  const history = useQuery({
    queryKey: [ROOT, 'hub-history'],
    queryFn: () => api.getHubHistory(),
    staleTime: 0,
    gcTime: 0,
  })

  // Сообщения сервера: история и принятые вопросы этого открытия чата
  const [messages, setMessages] = useState<HubMessage[]>([])
  // Вопросы на устройстве: ещё не приняты сервером
  const [queued, setQueued] = useState<QueuedQuestion[]>([])
  const [active, setActive] = useState(hubQueue.active())
  // Ожидания ответа, которые сорвались: остальные pending-ответы ждём сами.
  const [waitFailed, setWaitFailed] = useState<Record<string, true>>({})
  const seeded = useRef(false)
  const waits = useRef(new Map<string, AbortController>())
  const queuedRef = useRef(queued)
  queuedRef.current = queued

  // История приходит один раз: дальше разговор ведётся локально, иначе
  // перечитывание стёрло бы принятые вопросы, которых ещё нет в ответе.
  useEffect(() => {
    if (seeded.current || !history.data) return
    seeded.current = true
    setMessages((local) => {
      const known = new Set(history.data.map((m) => m.id))
      return [...history.data, ...local.filter((m) => !known.has(m.id))]
    })
  }, [history.data])

  // Очередь перечитывается из хранилища; ответ на устаревший запрос не затирает свежий
  const reloadSeq = useRef(0)
  const reload = useCallback(async () => {
    const my = ++reloadSeq.current
    try {
      const list = await hubQueue.list()
      if (my === reloadSeq.current) setQueued(list)
    } catch {
      // хранилище недоступно - остаётся то, что видно
    }
  }, [hubQueue])

  const accept = useCallback((e: Extract<HubQueueEvent, { type: 'sent' }>) => {
    // Вопрос принят: его место занимают вопрос и ожидаемый ответ сервера
    setMessages((list) => {
      const present = new Set(list.map((m) => m.id))
      const out = [...list]
      if (!present.has(e.userMessageId)) {
        out.push({ id: e.userMessageId, role: 'user', text: e.text, status: 'completed', createdAt: e.at })
      }
      if (!present.has(e.assistantMessageId)) {
        out.push({ id: e.assistantMessageId, role: 'assistant', text: '', status: 'pending', createdAt: e.at })
      }
      return out
    })
    setQueued((list) => list.filter((q) => q.id !== e.id))
  }, [])

  // Открыли чат: показать очередь и отправить её, если есть сеть.
  // Появилась сеть - отправить (то же делает приложение целиком, повтор склеится).
  useEffect(() => {
    void reload()
    const off = hubQueue.subscribe((e) => {
      if (e.userId !== hubQueue.owner()) return
      if (e.type === 'active') {
        setActive(e.id && e.phase ? { id: e.id, phase: e.phase } : null)
        return
      }
      // Принятый вопрос этого направления встаёт в ленту, даже если очередь
      // ещё не дочиталась (отправка при открытии чата)
      if (e.type === 'sent' && (e.assistantId === hubQueue.assistant() || queuedRef.current.some((q) => q.id === e.id))) {
        accept(e)
      }
      if (e.type === 'problem') platform.haptic('error')
      void reload()
    })
    const offOnline = onOnline(() => void hubQueue.kick())
    void hubQueue.kick()
    return () => {
      off()
      offOnline()
    }
  }, [accept, hubQueue, platform, reload])

  const replaceMessage = useCallback((message: HubMessage) => {
    setMessages((list) => list.map((m) => (m.id === message.id ? message : m)))
  }, [])

  // Ждём каждый неготовый ответ, пока экран открыт. Опрос идёт только пока
  // есть чего ждать; уход с экрана его останавливает.
  useEffect(() => {
    for (const m of messages) {
      if (!replyPending(m)) continue
      const id = m.id
      if (waitFailed[id] || waits.current.has(id)) continue
      const ctrl = new AbortController()
      waits.current.set(id, ctrl)
      api
        .waitHubReply(id, ctrl.signal)
        .then((reply) => {
          if (ctrl.signal.aborted) return
          replaceMessage(reply)
        })
        .catch(() => {
          if (ctrl.signal.aborted) return
          setWaitFailed((w) => ({ ...w, [id]: true }))
        })
        .finally(() => {
          if (waits.current.get(id) === ctrl) waits.current.delete(id)
        })
    }
  }, [api, messages, replaceMessage, waitFailed])

  useEffect(() => {
    const map = waits.current
    return () => {
      map.forEach((c) => c.abort())
      map.clear()
    }
  }, [])

  const show = useCallback((q: QueuedQuestion) => {
    setQueued((list) => (list.some((i) => i.id === q.id) ? list : [...list, q]))
  }, [])

  /** Отправить вопрос текстом. false - не удалось сохранить даже на устройстве. */
  const send = useCallback(
    async (text: string): Promise<boolean> => {
      const t = text.trim()
      if (!t) return true
      try {
        show(await hubQueue.addText(t))
      } catch {
        toast('Вопрос не сохранился на устройстве. Попробуйте ещё раз.')
        return false
      }
      platform.haptic('select')
      void hubQueue.kick()
      return true
    },
    [hubQueue, platform, show, toast],
  )

  /** Голосовой вопрос: распознается при отправке, контекст добавится к тексту. */
  const sendVoice = useCallback(
    async (audio: Blob, durationMs: number, contextLabel?: string): Promise<boolean> => {
      try {
        show(await hubQueue.addVoice(audio, durationMs, contextLabel))
      } catch {
        toast('Запись не сохранилась на устройстве. Запишите ещё раз или напишите текстом.')
        return false
      }
      platform.haptic('select')
      void hubQueue.kick()
      return true
    },
    [hubQueue, platform, show, toast],
  )

  /** Отправить ещё раз сейчас - тем же идентификатором. */
  const retry = useCallback((id: string) => void hubQueue.retry(id), [hubQueue])

  /** Удалить вопрос до отправки (на сервере его нет). */
  const discard = useCallback(
    async (id: string) => {
      if (await hubQueue.remove(id)) setQueued((list) => list.filter((q) => q.id !== id))
    },
    [hubQueue],
  )

  /** Ответ не дождались - проверить ещё раз (вопрос уже принят, заново не отправляем). */
  const recheck = useCallback((assistantMessageId: string) => {
    setWaitFailed((w) => {
      const next = { ...w }
      delete next[assistantMessageId]
      return next
    })
  }, [])

  const items: HubItem[] = [
    ...messages.map((message): HubItem => ({ kind: 'message', message })),
    ...queued.map((question): HubItem => ({ kind: 'queued', question, phase: queuedPhase(question, active, online) })),
  ]

  return {
    items,
    history,
    online,
    /** Очередь переживёт закрытие приложения (IndexedDB открылся). */
    persistent: hubQueue.persistent(),
    waitFailed,
    send,
    sendVoice,
    retry,
    discard,
    recheck,
  }
}
